import {
  AKTIVRETTUNG_MAX_POINTS,
  SRHT_EXAM_STATION_AKTIVRETTUNG,
  SRHT_EXAM_STATION_EINZELUEBERPRUEFUNG,
  SRHT_EXAM_STATION_FALLSCHUTZ,
  SRHT_EXAM_STATION_FITNESS_COORDINATION,
  SRHT_EXAM_STATION_GRUPPENPRUEFUNG,
  getAktivrettungGrade,
  getAktivrettungOverallPoints,
  getEffectiveFitnessCoordinationTotalMs,
  getEinzelueberpruefungGrade,
  getEinzelueberpruefungOverallPoints,
  getFitnessCoordinationGrade,
  getGroupPracticalGrade,
  getGroupPracticalOverallPoints,
} from './srhtExamPracticalSheets.js';

export const SRHT_EXAM_WRITTEN_DISCIPLINE_CODE = 'written_exam';

export const SRHT_EXAM_FINAL_DISCIPLINES = [
  {
    code: SRHT_EXAM_WRITTEN_DISCIPLINE_CODE,
    title: 'Schriftliche Prüfung',
    shortTitle: 'Schriftliche Prüfung',
    kind: 'written',
    oralRetakePriority: null,
  },
  {
    code: SRHT_EXAM_STATION_FITNESS_COORDINATION,
    title: 'Fitness & Koordination',
    shortTitle: 'Fitness & Koordination',
    kind: 'practical',
    oralRetakePriority: 4,
  },
  {
    code: SRHT_EXAM_STATION_AKTIVRETTUNG,
    title: 'Kameradenrettung',
    shortTitle: 'Kameradenrettung',
    kind: 'practical',
    oralRetakePriority: 1,
  },
  {
    code: SRHT_EXAM_STATION_FALLSCHUTZ,
    title: 'Person im Fallschutz',
    shortTitle: 'Person im Fallschutz',
    kind: 'practical',
    oralRetakePriority: 2,
  },
  {
    code: SRHT_EXAM_STATION_GRUPPENPRUEFUNG,
    title: 'Tragenrettung',
    shortTitle: 'Tragenrettung',
    kind: 'practical',
    oralRetakePriority: 3,
  },
  {
    code: SRHT_EXAM_STATION_EINZELUEBERPRUEFUNG,
    title: 'Einzelüberprüfung',
    shortTitle: 'Einzelüberprüfung',
    kind: 'practical',
    oralRetakePriority: 5,
  },
];

const DISCIPLINE_META_BY_CODE = Object.fromEntries(
  SRHT_EXAM_FINAL_DISCIPLINES.map((discipline) => [discipline.code, discipline]),
);

const GRADE_TO_VAP_POINTS = {
  1: 15,
  2: 12,
  3: 9,
  4: 6,
  5: 3,
  6: 0,
};

const POINT_LABEL_RANGES = [
  { min: 14, max: 15, label: 'Sehr gut' },
  { min: 11, max: 13, label: 'Gut' },
  { min: 8, max: 10, label: 'Befriedigend' },
  { min: 5, max: 7, label: 'Ausreichend' },
  { min: 2, max: 4, label: 'Mangelhaft' },
  { min: 0, max: 1, label: 'Ungenügend' },
];

function roundToTwoDecimals(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 0;
  }
  return Math.round(numericValue * 100) / 100;
}

function normalizeInteger(value) {
  const numericValue = Number(value);
  return Number.isInteger(numericValue) ? numericValue : null;
}

function getDisciplineMeta(code) {
  return DISCIPLINE_META_BY_CODE[String(code || '').trim()] || null;
}

export function getSrhtExamGradeLabelFromPoints(points) {
  const normalizedPoints = Number(points);
  if (!Number.isFinite(normalizedPoints)) {
    return 'Noch offen';
  }

  const matchingRange = POINT_LABEL_RANGES.find((range) => normalizedPoints >= range.min && normalizedPoints <= range.max);
  return matchingRange?.label || 'Noch offen';
}

export function getSrhtExamGradeToneFromPoints(points) {
  const normalizedPoints = Number(points);
  if (!Number.isFinite(normalizedPoints)) {
    return 'default';
  }
  if (normalizedPoints >= 11) {
    return 'success';
  }
  if (normalizedPoints >= 5) {
    return 'warning';
  }
  return 'error';
}

export function getSrhtExamRepresentativePointsFromGrade(grade) {
  const normalizedGrade = Number(grade);
  return Object.prototype.hasOwnProperty.call(GRADE_TO_VAP_POINTS, normalizedGrade)
    ? GRADE_TO_VAP_POINTS[normalizedGrade]
    : null;
}

function getGroupForCandidateResult(sheetRecord = {}, candidateId) {
  const rawPayload = sheetRecord?.rawPayload || {};
  const result = rawPayload?.resultsByCandidateId?.[String(candidateId)] || null;
  if (!result) {
    return { result: null, group: null };
  }

  const groups = Array.isArray(rawPayload?.groups) ? rawPayload.groups : [];
  const explicitGroup = groups.find((entry) => String(entry?.id || '') === String(result.groupId || '')) || null;
  if (explicitGroup) {
    return { result, group: explicitGroup };
  }

  const fallbackGroup = groups.find((entry) => (
    Array.isArray(entry?.participants)
      && entry.participants.some((participant) => String(participant?.candidateId || '') === String(candidateId))
  )) || null;

  return { result, group: fallbackGroup };
}

function buildWrittenDiscipline(candidate) {
  const writtenResult = candidate?.writtenResult || null;
  const representativePoints = getSrhtExamRepresentativePointsFromGrade(writtenResult?.grade);
  const finalized = writtenResult?.status === 'finalized';

  return {
    ...getDisciplineMeta(SRHT_EXAM_WRITTEN_DISCIPLINE_CODE),
    status: finalized ? 'finalized' : writtenResult ? 'draft' : 'missing',
    complete: Boolean(finalized && representativePoints !== null),
    sourceGrade: normalizeInteger(writtenResult?.grade),
    points: representativePoints,
    rawValue: normalizeInteger(writtenResult?.points),
    rawValueLabel: writtenResult?.points === 0 || writtenResult?.points ? `${Number(writtenResult.points)} / 129 Punkte` : 'Noch offen',
    gradeLabel: representativePoints === null ? 'Noch offen' : getSrhtExamGradeLabelFromPoints(representativePoints),
  };
}

function buildFitnessDiscipline(sheetRecord, candidateId) {
  const result = sheetRecord?.rawPayload?.resultsByCandidateId?.[String(candidateId)] || null;
  const totalMs = getEffectiveFitnessCoordinationTotalMs(result || {});
  const grade = getFitnessCoordinationGrade(totalMs);
  const representativePoints = getSrhtExamRepresentativePointsFromGrade(grade);
  const finalized = result?.status === 'finalized';

  return {
    ...getDisciplineMeta(SRHT_EXAM_STATION_FITNESS_COORDINATION),
    status: finalized ? 'finalized' : result ? 'draft' : 'missing',
    complete: Boolean(finalized && representativePoints !== null),
    sourceGrade: normalizeInteger(grade),
    points: representativePoints,
    rawValue: totalMs || null,
    rawValueLabel: totalMs ? `${Math.round(totalMs / 1000)} s` : 'Noch offen',
    gradeLabel: representativePoints === null ? 'Noch offen' : getSrhtExamGradeLabelFromPoints(representativePoints),
  };
}

function buildAktivrettungDiscipline(sheetRecord, candidateId) {
  const result = sheetRecord?.rawPayload?.resultsByCandidateId?.[String(candidateId)] || null;
  const overallPoints = getAktivrettungOverallPoints(result || {});
  const grade = getAktivrettungGrade(overallPoints);
  const representativePoints = getSrhtExamRepresentativePointsFromGrade(grade);
  const finalized = result?.status === 'finalized';

  return {
    ...getDisciplineMeta(SRHT_EXAM_STATION_AKTIVRETTUNG),
    status: finalized ? 'finalized' : result ? 'draft' : 'missing',
    complete: Boolean(finalized && representativePoints !== null),
    sourceGrade: normalizeInteger(grade),
    points: representativePoints,
    rawValue: overallPoints,
    rawValueLabel: overallPoints === null ? 'Noch offen' : `${roundToTwoDecimals(overallPoints)} / ${AKTIVRETTUNG_MAX_POINTS} Punkte`,
    gradeLabel: representativePoints === null ? 'Noch offen' : getSrhtExamGradeLabelFromPoints(representativePoints),
  };
}

function buildGroupPracticalDiscipline(sheetRecord, candidateId, stationCode) {
  const { result, group } = getGroupForCandidateResult(sheetRecord, candidateId);
  const overallPoints = getGroupPracticalOverallPoints(result || {}, group || {}, stationCode);
  const grade = getGroupPracticalGrade(overallPoints, stationCode);
  const representativePoints = getSrhtExamRepresentativePointsFromGrade(grade);
  const finalized = result?.status === 'finalized';

  return {
    ...getDisciplineMeta(stationCode),
    status: finalized ? 'finalized' : result ? 'draft' : 'missing',
    complete: Boolean(finalized && representativePoints !== null),
    sourceGrade: normalizeInteger(grade),
    points: representativePoints,
    rawValue: overallPoints,
    rawValueLabel: overallPoints === null ? 'Noch offen' : `${roundToTwoDecimals(overallPoints)} / 40 Punkte`,
    gradeLabel: representativePoints === null ? 'Noch offen' : getSrhtExamGradeLabelFromPoints(representativePoints),
  };
}

function buildEinzelDiscipline(sheetRecord, candidateId) {
  const result = sheetRecord?.rawPayload?.resultsByCandidateId?.[String(candidateId)] || null;
  const overallPoints = getEinzelueberpruefungOverallPoints(result || {});
  const grade = getEinzelueberpruefungGrade(overallPoints);
  const representativePoints = getSrhtExamRepresentativePointsFromGrade(grade);
  const finalized = result?.status === 'finalized';

  return {
    ...getDisciplineMeta(SRHT_EXAM_STATION_EINZELUEBERPRUEFUNG),
    status: finalized ? 'finalized' : result ? 'draft' : 'missing',
    complete: Boolean(finalized && representativePoints !== null),
    sourceGrade: normalizeInteger(grade),
    points: representativePoints,
    rawValue: overallPoints,
    rawValueLabel: overallPoints === null ? 'Noch offen' : `${roundToTwoDecimals(overallPoints)} / 120 Punkte`,
    gradeLabel: representativePoints === null ? 'Noch offen' : getSrhtExamGradeLabelFromPoints(representativePoints),
  };
}

function buildDisciplineSummaries(candidate, practicalSheets = {}) {
  return [
    buildWrittenDiscipline(candidate),
    buildFitnessDiscipline(practicalSheets?.[SRHT_EXAM_STATION_FITNESS_COORDINATION], candidate?.id),
    buildAktivrettungDiscipline(practicalSheets?.[SRHT_EXAM_STATION_AKTIVRETTUNG], candidate?.id),
    buildGroupPracticalDiscipline(practicalSheets?.[SRHT_EXAM_STATION_FALLSCHUTZ], candidate?.id, SRHT_EXAM_STATION_FALLSCHUTZ),
    buildGroupPracticalDiscipline(practicalSheets?.[SRHT_EXAM_STATION_GRUPPENPRUEFUNG], candidate?.id, SRHT_EXAM_STATION_GRUPPENPRUEFUNG),
    buildEinzelDiscipline(practicalSheets?.[SRHT_EXAM_STATION_EINZELUEBERPRUEFUNG], candidate?.id),
  ];
}

function buildOralRetakeLookup(oralRetakes = []) {
  return Object.fromEntries(
    (Array.isArray(oralRetakes) ? oralRetakes : [])
      .filter((entry) => entry?.candidateId)
      .map((entry) => [String(entry.candidateId), entry]),
  );
}

function getOverallWeightedPoints(disciplines) {
  const writtenDiscipline = disciplines.find((discipline) => discipline.code === SRHT_EXAM_WRITTEN_DISCIPLINE_CODE) || null;
  const practicalDisciplines = disciplines.filter((discipline) => discipline.kind === 'practical');
  if (!writtenDiscipline || writtenDiscipline.points === null || practicalDisciplines.length !== 5 || practicalDisciplines.some((discipline) => discipline.points === null)) {
    return null;
  }

  const practicalAverage = practicalDisciplines.reduce((sum, discipline) => sum + Number(discipline.points || 0), 0) / practicalDisciplines.length;
  return roundToTwoDecimals((Number(writtenDiscipline.points) / 3) + ((practicalAverage * 2) / 3));
}

function getOverallPercent(overallPoints) {
  if (!Number.isFinite(Number(overallPoints))) {
    return null;
  }
  return roundToTwoDecimals((Number(overallPoints) / 15) * 100);
}

function getOverallWholePoints(overallPoints) {
  if (!Number.isFinite(Number(overallPoints))) {
    return null;
  }
  return Math.max(0, Math.min(15, Math.round(Number(overallPoints))));
}

function buildPassSummary(disciplines, overallWholePoints, oralRetakeRecord) {
  const countedDisciplines = disciplines.filter((discipline) => discipline.points !== null);
  const belowTwoCount = countedDisciplines.filter((discipline) => Number(discipline.points) < 2).length;
  const belowFiveCount = countedDisciplines.filter((discipline) => Number(discipline.points) < 5).length;
  const passedWithoutOralRetake = overallWholePoints !== null
    && overallWholePoints >= 5
    && belowTwoCount === 0
    && belowFiveCount <= 1;
  const oralRetakePassed = String(oralRetakeRecord?.outcome || '').trim() === 'passed';

  return {
    passed: passedWithoutOralRetake || oralRetakePassed,
    passedWithoutOralRetake,
    oralRetakePassed,
    belowTwoCount,
    belowFiveCount,
  };
}

export function getSrhtOralRetakeRecommendation(disciplines, overallWholePoints) {
  const practicalFailures = disciplines
    .filter((discipline) => discipline.kind === 'practical' && discipline.points !== null && discipline.points >= 2 && discipline.points <= 4)
    .sort((left, right) => Number(left.oralRetakePriority || 999) - Number(right.oralRetakePriority || 999));

  const allFailures = disciplines.filter((discipline) => discipline.points !== null && discipline.points >= 2 && discipline.points <= 4);
  const ungenuegendCount = disciplines.filter((discipline) => discipline.points !== null && discipline.points < 2).length;

  const eligible = overallWholePoints !== null
    && overallWholePoints >= 5
    && practicalFailures.length === 2
    && allFailures.length === 2
    && ungenuegendCount === 0;

  return {
    eligible,
    recommendedDiscipline: eligible ? practicalFailures[0] : null,
    practicalFailures,
  };
}

export function getRequiredOralRetakeAdjustedPoints(candidateSummary, stationCode) {
  if (!candidateSummary?.oralRetake?.eligible || !stationCode) {
    return null;
  }

  const targetDiscipline = (candidateSummary.baseDisciplines || []).find((discipline) => discipline.code === stationCode);
  if (!targetDiscipline || targetDiscipline.kind !== 'practical' || targetDiscipline.points === null) {
    return null;
  }

  const currentOverall = Number(candidateSummary.overallPointsRaw || 0);
  const deltaToPass = Math.max(0, 5 - currentOverall);
  const practicalPointIncreaseNeeded = Math.ceil((deltaToPass * 15) / 2);
  const adjustedPoints = Math.max(Number(targetDiscipline.points || 0), 5, Number(targetDiscipline.points || 0) + practicalPointIncreaseNeeded);
  return Math.min(15, adjustedPoints);
}

function buildCandidateSummary(candidate, practicalSheets, oralRetakeRecord) {
  const baseDisciplines = buildDisciplineSummaries(candidate, practicalSheets);
  const allComplete = baseDisciplines.every((discipline) => discipline.complete);
  const overallPointsRaw = allComplete ? getOverallWeightedPoints(baseDisciplines) : null;
  const overallWholePoints = getOverallWholePoints(overallPointsRaw);
  const oralRetake = getSrhtOralRetakeRecommendation(baseDisciplines, overallWholePoints);
  const passSummary = buildPassSummary(baseDisciplines, overallWholePoints, oralRetakeRecord);
  const percentScore = getOverallPercent(overallPointsRaw);
  const gradeLabel = overallWholePoints === null ? 'Noch offen' : getSrhtExamGradeLabelFromPoints(overallWholePoints);
  const sortablePoints = overallPointsRaw === null ? -1 : overallPointsRaw;

  return {
    candidateId: Number(candidate.id),
    fullName: candidate.fullName || '-',
    courseLabel: candidate.courseLabel || '-',
    complete: allComplete,
    baseDisciplines,
    disciplines: baseDisciplines,
    oralRetake: {
      ...oralRetake,
      record: oralRetakeRecord || null,
    },
    overallPointsRaw,
    overallWholePoints,
    percentScore,
    gradeLabel,
    passed: allComplete ? passSummary.passed : false,
    failedDisciplineCount: passSummary.belowFiveCount,
    criticalDisciplineCount: passSummary.belowTwoCount,
    sortablePoints,
  };
}

export function buildSrhtFinalEvaluation(examDetail = {}) {
  const candidates = Array.isArray(examDetail?.candidates) ? examDetail.candidates : [];
  const practicalSheets = examDetail?.practicalSheets || {};
  const oralRetakeLookup = buildOralRetakeLookup(examDetail?.oralRetakes || []);

  const candidateSummaries = candidates
    .map((candidate) => buildCandidateSummary(candidate, practicalSheets, oralRetakeLookup[String(candidate.id)] || null))
    .sort((left, right) => {
      if (right.sortablePoints !== left.sortablePoints) {
        return right.sortablePoints - left.sortablePoints;
      }
      return String(left.fullName || '').localeCompare(String(right.fullName || ''), 'de');
    })
    .map((summary, index) => ({
      ...summary,
      rankPosition: index + 1,
    }));

  return {
    candidates: candidateSummaries,
    completedCandidateCount: candidateSummaries.filter((candidate) => candidate.complete).length,
  };
}